import {useState, useEffect} from 'react';
import {Link} from 'react-router-dom';
import Tippy from '@tippyjs/react';
import styled from 'styled-components';
import {NumericHeader, NumericCell} from '../../../components/Table/TableStyles';
import {EditedParticipation} from '../EditedProject';
import {PlantingProjectType} from '../../../entities';
import {HeaderContainer, SubSectionHeader} from '../../../components/Form/FormStyles';
import {AddButton} from '../../../components/Buttons/AddButton';
import {EditButton} from '../../../components/Buttons/EditButton';
import {RemoveButton} from '../../../components/Buttons/RemoveButton';
import {ParticipationModal} from '../MultipleProjectTypes/ParticipationModal';

export const PartnersTable = styled.table.attrs({className: 'table'})`
    margin-bottom: 1rem;
`;

type PartnerOption = {Id: number; Name: string};

type ModalState =
    | {open: false}
    | {open: true; mode: 'add'}
    | {open: true; mode: 'edit'; participationId: number; partnerName: string};

export function CarbonFundingPartnersControl({
    participations,
    projectId,
    onParticipationChange
}: {
    participations: EditedParticipation[];
    projectId: number;
    onParticipationChange: () => void;
}) {
    const [partners, setPartners] = useState<PartnerOption[]>([]);
    const [modal, setModal] = useState<ModalState>({open: false});
    const [deleting, setDeleting] = useState<number | null>(null);

    useEffect(() => {
        fetch('/api/partners')
            .then((r) => r.json())
            .then((data: PartnerOption[]) => setPartners(data));
    }, []);

    async function handleDelete(participationId: number) {
        setDeleting(participationId);
        try {
            await fetch(`/api/participations/${participationId}`, {method: 'DELETE'});
            onParticipationChange();
        } finally {
            setDeleting(null);
        }
    }

    return (
        <>
            <HeaderContainer>
                <SubSectionHeader>Funding partners</SubSectionHeader>
                <Tippy content={projectId ? 'Add participation' : 'Save the project first to add participations'}>
                    <span>
                        <AddButton
                            onClick={() => setModal({open: true, mode: 'add'})}
                            disabled={!projectId}
                        />
                    </span>
                </Tippy>
            </HeaderContainer>

            <PartnersTable>
                <thead>
                    <tr>
                        <th>Partner</th>
                        <NumericHeader>Carbon Credits</NumericHeader>
                        <NumericHeader>Purchase year</NumericHeader>
                        <th></th>
                    </tr>
                </thead>
                <tbody>
                    {participations?.map((ppn) => (
                        <tr key={ppn.Id}>
                            <td>
                                <Link to={`/partners/${ppn.PartnerId}`}>{ppn.Partner.Name}</Link>
                            </td>
                            <NumericCell>{ppn.CarbonCreditsTransacted}</NumericCell>
                            <NumericCell>{ppn.FundedYear ?? ''}</NumericCell>
                            <td>
                                <div style={{display: 'flex', alignItems: 'center', gap: '4px'}}>
                                    <Tippy content="Edit">
                                        <span>
                                            <EditButton
                                                onClick={() =>
                                                    setModal({
                                                        open: true,
                                                        mode: 'edit',
                                                        participationId: ppn.Id,
                                                        partnerName: ppn.Partner.Name
                                                    })
                                                }
                                            />
                                        </span>
                                    </Tippy>
                                    <Tippy content="Delete">
                                        <span>
                                            <RemoveButton
                                                onClick={() => handleDelete(ppn.Id)}
                                                disabled={deleting === ppn.Id}
                                            />
                                        </span>
                                    </Tippy>
                                </div>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </PartnersTable>

            <ParticipationModal
                show={modal.open}
                mode={modal.open ? modal.mode : 'add'}
                participationId={modal.open && modal.mode === 'edit' ? modal.participationId : undefined}
                partnerName={modal.open && modal.mode === 'edit' ? modal.partnerName : undefined}
                projectId={projectId}
                projectType={PlantingProjectType.Carbon}
                partners={partners}
                onSaved={onParticipationChange}
                onClose={() => setModal({open: false})}
            />
        </>
    );
}
